// The head watcher (spec/dashboard.md section 4.6): the game's event stream filtered to `branch.head` of this branch
// (spec/api-conventions.md section 21.5) moves the head as soon as a commit lands; while the stream is not connected
// (before it opens, while it reconnects, or when the server refused it) the old poll runs instead: GET the branch with
// If-None-Match every 15 s (a 304 is free) and when the tab regains focus; when the head moves, compare and flag the
// drafts whose documents someone else changed. A commit this client made itself is not a remote change.
import type { ApiClient } from '../api/client';
import { ops } from '../api/operations';
import type { StreamFrame, StreamState } from '../api/stream';
import type { ResourceEvent } from '../api/types';
import { entityKey, type EntityKey, type Workspace } from './Workspace';

export interface HeadWatcherOptions {
  client: ApiClient;
  org: string;
  game: string;
  branch: string;
  /** the branch's id: an event names it in `branchId` */
  branchId?: string | null;
  workspace: Workspace;
  intervalMs?: number;
  /** called after a head move, with the paths that changed (the pages invalidate their reads) */
  onChange?: (info: { from: string | null; to: string; paths: string[] }) => void;
  /** false: poll only (no stream) */
  stream?: boolean;
  /** the stream client's wait between reconnects (tests) */
  streamSleep?: (ms: number, signal: AbortSignal) => Promise<void>;
  onStreamState?: (state: StreamState) => void;
  setTimeoutImpl?: typeof setTimeout;
  clearTimeoutImpl?: typeof clearTimeout;
}

/** `documents/<template>/<uuid>.json` → `document:<uuid>` */
export function keyOfPath(path: string): EntityKey | null {
  const m = /^documents\/[^/]+\/([0-9a-fA-F-]{36})\.json$/.exec(path);
  return m ? entityKey('document', m[1]!.toLowerCase()) : null;
}

/** The payload of a `branch.head` event. */
export interface BranchHeadData {
  branch?: string | null;
  commit?: string | null;
  parent?: string | null;
  paths?: string[] | null;
}

export class HeadWatcher {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private stopped = true;
  private checking: Promise<void> | null = null;
  private abort: AbortController | null = null;
  private streamState: StreamState = 'stopped';
  private everConnected = false;

  constructor(private readonly o: HeadWatcherOptions) {}

  start(): void {
    this.stopped = false;
    void this.check();
    this.schedule();
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', this.onVisibility);
    if (this.o.stream !== false) this.openStream();
  }

  stop(): void {
    this.stopped = true;
    this.unschedule();
    this.abort?.abort();
    this.abort = null;
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', this.onVisibility);
  }

  /** The stream's state: `connected` means the poll is paused. */
  get state(): StreamState {
    return this.streamState;
  }

  private openStream(): void {
    const { client, org, game, branch } = this.o;
    const abort = new AbortController();
    this.abort = abort;
    void client.stream(ops.streamGameEvents, {
      path: { org, game },
      query: { kinds: 'branch.head', branch },
      signal: abort.signal,
      sleep: this.o.streamSleep,
      onFrame: (frame) => this.onFrame(frame),
      onStateChange: (state) => {
        if (abort.signal.aborted && state !== 'stopped') return;
        this.streamState = state;
        this.o.onStreamState?.(state);
        if (this.stopped) return;
        if (state === 'connected') {
          // the stream carries the moves now; the first connection starts at the live tail, so catch up once
          this.unschedule();
          if (!this.everConnected) void this.check();
          this.everConnected = true;
        } else if (this.timer === null) {
          this.schedule(); // the fallback while the stream is down or refused
        }
      },
    });
  }

  private onVisibility = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') void this.check();
  };

  private unschedule(): void {
    if (this.timer !== null) (this.o.clearTimeoutImpl ?? clearTimeout)(this.timer);
    this.timer = null;
  }

  private schedule(): void {
    if (this.stopped || this.streamState === 'connected') return;
    this.timer = (this.o.setTimeoutImpl ?? setTimeout)(() => {
      this.timer = null;
      void this.check().finally(() => this.schedule());
    }, this.o.intervalMs ?? 15000);
  }

  /** A `branch.head` frame of the stream: the same head move the poll would make, with the paths the event carries. */
  private onFrame(frame: StreamFrame): void {
    if (this.stopped || frame.event !== 'branch.head') return;
    const event = frame.data as Partial<ResourceEvent> | null;
    if (!event || typeof event !== 'object') return;
    const data = (event.data ?? {}) as BranchHeadData;
    const mine = data.branch === this.o.branch || (!!this.o.branchId && event.branchId === this.o.branchId);
    if (!mine || !data.commit) return;
    const { workspace, client } = this.o;
    const previous = workspace.head;
    const commit = data.commit;
    if (commit === previous) return;
    const paths = (data.paths ?? []).filter((p): p is string => typeof p === 'string');
    // this client's own write (the request that produced it, or a head the workspace already took from a response)
    if (client.sentRequest(event.requestId) || workspace.produced(commit)) {
      workspace.setHead(commit);
      return;
    }
    const keys = paths.map(keyOfPath).filter((k): k is EntityKey => k !== null);
    workspace.flagRemote(keys, { by: event.actor?.label ?? undefined, at: event.occurredAt ?? undefined, sha: commit });
    workspace.noteRemoteChanges(paths);
    workspace.setHead(commit, { remote: true });
    this.o.onChange?.({ from: previous, to: commit, paths });
  }

  /** One poll; safe to call at any time (re-entrancy is coalesced). */
  check(): Promise<void> {
    if (this.checking) return this.checking;
    this.checking = this.run().finally(() => {
      this.checking = null;
    });
    return this.checking;
  }

  private async run(): Promise<void> {
    const { client, org, game, branch, workspace } = this.o;
    const asked = workspace.head;
    let head: string | null;
    try {
      const result = await client.call(ops.getBranch, { path: { org, game, branch }, cached: true });
      head = result.data.head ?? null;
      workspace.setProtected(result.data.isProtected);
    } catch {
      return; // a transport failure is not a head move; the next tick tries again
    }
    const previous = workspace.head;
    if (previous !== asked) return; // the stream or a save moved the head meanwhile: this answer may be older
    if (head === null || head === previous) {
      if (previous === null && head !== null) workspace.setHead(head, { remote: true });
      return;
    }
    if (previous === null || workspace.produced(head)) {
      workspace.setHead(head, { remote: true });
      return;
    }
    let paths: string[] = [];
    let by: string | undefined;
    let at: string | undefined;
    try {
      const diff = await client.data(ops.compareCommits, { path: { org, game, branch }, query: { from: previous, to: head } });
      paths = diff.changes.map((c) => c.path);
      by = diff.commit.authorName ?? undefined;
      at = diff.commit.authoredAt ?? undefined;
    } catch {
      // the compare failed (the old head may be gone after a force push): every draft is flagged
      paths = workspace.all.map((d) => (d.kind === 'document' ? `documents/${d.template ?? '_'}/${d.id}.json` : ''));
    }
    const keys = paths.map(keyOfPath).filter((k): k is EntityKey => k !== null);
    workspace.flagRemote(keys, { by, at, sha: head });
    workspace.noteRemoteChanges(paths);
    workspace.setHead(head, { remote: true });
    this.o.onChange?.({ from: previous, to: head, paths });
  }
}
